import { useCallback, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useReceivedEventTimeout } from 'common/hooks';
import { callAnswerTimeoutMs } from 'features/call/call-received/helpers';
import { Hotkey } from 'config/enums';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  handleCallNotReceived,
  shouldCallBeAnswerTimeout,
  takeCall,
} from 'features/call/call-base/store';
import {
  isDeafToHearingSelector,
  selectCanRelayTransferCallToMercury,
} from 'features/call/call-base/store/selectors';
import { isDeafConnectedSelector } from 'features/call/call-deaf/store';
import { isPrimaryHearingConnectedSelector } from 'features/call/call-hearing/store';
import { connectCall } from 'features/call/call-ui-state/store';
import { SipSessionEventBus } from 'features/sip/services';

export const useCallReceivedTakeCall = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const isDeafToHearing = useAppSelector(isDeafToHearingSelector);
  const isDeafConnected = useAppSelector(isDeafConnectedSelector);
  const isHearingConnected = useAppSelector(isPrimaryHearingConnectedSelector);
  const canRelayTransferCallToMercury = useAppSelector(
    selectCanRelayTransferCallToMercury
  );
  const { setTimeout, removeTimeout } = useReceivedEventTimeout();

  const handleCallTimeout = useCallback(async () => {
    const shouldAnswerTimeoutOccur = dispatch(shouldCallBeAnswerTimeout());
    if (shouldAnswerTimeoutOccur) {
      SipSessionEventBus.endCall$.next();
      await dispatch(handleCallNotReceived());
    }
  }, [dispatch]);

  const handleTakeCall = useCallback(async () => {
    try {
      setIsLoading(true);
      await dispatch(takeCall()).unwrap();
      if (!isDeafToHearing) {
        dispatch(connectCall());
      }
      // If this is false, RNS will send an initiate dial event to Mercury so we won't want to time out the call
      if (canRelayTransferCallToMercury) {
        setTimeout(handleCallTimeout, callAnswerTimeoutMs());
      }
    } catch (error) {
      console.error('Error taking call', error);
    }
  }, [
    dispatch,
    isDeafToHearing,
    canRelayTransferCallToMercury,
    handleCallTimeout,
    setTimeout,
  ]);

  useHotkeys(
    Hotkey.TakeCall,
    () => {
      if (!isLoading) {
        handleTakeCall();
      }
    },
    [handleTakeCall, isLoading]
  );

  useEffect(() => {
    // clear timer if call is connected or hearing party is connected.
    if (isDeafConnected || isHearingConnected) {
      removeTimeout();
    }
  }, [isDeafConnected, isHearingConnected, removeTimeout]);

  return { handleTakeCall, isLoading };
};
